import type { App } from '../types/app';
import { buildAppContextLines } from './appContextPrompt';

export type ApplePlacement = 'header' | 'search-results';
export interface AppleCrop { x: number; y: number; zoom: number }
export const APPLE_CREATIVE_PRESETS = {
  header: [
    { width: 5244, height: 2950, label: '5244 × 2950 · Universal' },
    { width: 3840, height: 1646, label: '3840 × 1646 · Wide header' },
  ],
  'search-results': [
    { width: 5244, height: 2950, label: '5244 × 2950 · Universal' },
    { width: 3840, height: 2560, label: '3840 × 2560 · Large search' },
    { width: 1920, height: 1280, label: '1920 × 1280 · Search' },
  ],
};
export const APPLE_ASSET_GUIDE = 'https://developer.apple.com/app-store/asset-best-practices/';
export const APPLE_ASSET_SPECS = 'https://developer.apple.com/help/app-store-connect/reference/app-information/creative-assets-specifications';

export function buildAppleCreativePrompt(app: App, placement: ApplePlacement, width: number, height: number, direction: string) {
  return [
    `Create App Store ${placement === 'header' ? 'product page header' : 'search results'} artwork for ${app.name}.`,
    `Compose for a final ${width} × ${height} landscape canvas (${(width / height).toFixed(3)}:1). It will be cropped and resized.`,
    placement === 'header'
      ? 'Express the brand through one clear, compelling visual idea for a first-time visitor.'
      : 'Make the app’s purpose immediately clear through its actual experience or relevant content. Do not invent interface features.',
    'Keep the focal subject centered with generous margins for device cropping. Extend the background to every edge.',
    'Use polished, distinctive artwork related to the app. No text overlays, watermarks, prices, URLs, awards, Apple badges, or other marketplace logos. Suitable for all ages.',
    'Use an opaque background. Avoid tiny details and clutter. Preserve a clear visual hierarchy.',
    ...buildAppContextLines(app),
    `Creative direction: ${direction.trim() || 'Choose an art direction that fits the app’s purpose and brand.'}`,
  ].join('\n');
}

export function getAppleCropRect(sourceWidth: number, sourceHeight: number, width: number, height: number, crop: AppleCrop) {
  const scale = Math.max(width / sourceWidth, height / sourceHeight) * Math.min(3, Math.max(1, crop.zoom));
  const w = Math.min(sourceWidth, Math.max(1, Math.round(width / scale)));
  const h = Math.min(sourceHeight, Math.max(1, Math.round(height / scale)));
  return { x: Math.round((sourceWidth - w) * crop.x / 100), y: Math.round((sourceHeight - h) * crop.y / 100), width: w, height: h };
}
